import { DateTime } from 'luxon';
import React from 'react';

import { IBaseProps } from '../../utils';

import { Error } from '../../Error';

import { InputSizes, ValidationStates } from '../enums';
import { NumberInput } from '../NumberInput';

export interface IYearInputProps extends IBaseProps {
  id?: string;
  /** The current value of the input that is displayed to the user */
  value?: string;
  /** Validation state of the input which can be either success or error */
  validationState?: ValidationStates;
  /** Set to true if you want to disable the input from accepting changes from the user */
  disabled?: boolean;
  /** Determines if field is required */
  required?: boolean;
  /** Whether or not to display the error message when the date is invalid */
  showErrorMessage?: boolean;
  /** Application level error message that the component will display regardless of internal error state */
  errorMessage?: string;
  /** This callback is executed anytime validation happens in the date input component.
   * Returns true or false for isYearValid, the current year being validated, and whether or not the year is complete
   */
  onYearValid?(isDateValid: boolean, date: string, isCompleteDate: boolean, msg: string): void;
  /** Application level error message that the component will display regardless of internal error state */
  onChange?(newValue: string): void;
}

export function createYearInput(sizeType: InputSizes) {
  const InputComponent = NumberInput[sizeType];

  return class YearInputBase extends React.Component<IYearInputProps, any> {
    static defaultProps = {
      onChange: () => {},
      onYearValid: () => {}
    };

    static displayName = `Input.Year.${sizeType}`;

    constructor(props) {
      super(props);

      const validationData = this.getValidationData(this.getFormattedDate(props.value || ''));
      this.state = {
        dateString: props.value,
        validationState: props.value && props.value.length > 0 ? validationData.validationState : null,
        errorMessage: props.errorMessage
      };
    }

    render() {
      // Make sure that we set the validation state correctly if there's
      // an error this component has detected in the input.
      const hasLocalValidationError = this.state.validationState === ValidationStates.Error || this.props.errorMessage;
      const validationState = hasLocalValidationError ? this.state.validationState : this.props.validationState;
      const { showErrorMessage = true } = this.props;

      const renderInputComponent = () => (
        <InputComponent
          className={this.props.className}
          id={this.props.id}
          dataTest={this.props.dataTest}
          disabled={this.props.disabled}
          required={this.props.required}
          value={this.state.dateString}
          validationState={validationState}
          onBlur={this.onBlur}
          onChange={this.onChange}
          placeholder="YYYY"
          maxLength={4}
        />
      );

      return showErrorMessage ? (
        <>
          {renderInputComponent()}
          {this.state.errorMessage && <Error>{this.state.errorMessage}</Error>}
        </>
      ) : (
        renderInputComponent()
      );
    }

    //==== User Interaction Functions ===========================================
    onBlur = (dateValueFromInput) => {
      this.updateDateValidationState(dateValueFromInput);
    };

    onChange = (dateValueFromInput: string) => {
      this.props.onChange(dateValueFromInput);
      this.setState({ dateString: dateValueFromInput });
      if (dateValueFromInput.length >= 4) {
        this.updateDateValidationState(dateValueFromInput);
      }
    };

    //==== Formatting Functions ================================================
    getFormattedDate = (dateValueFromInput) => {
      return DateTime.fromFormat(dateValueFromInput, 'yyyy');
    };

    //==== Validation Functions=================================================
    getValidationData = (dateTime: DateTime) => {
      const isValidDate = dateTime.isValid;

      const errorMessage = 'The date specified is invalid. Enter a year YYYY.';
      const validationState = isValidDate ? ValidationStates.Success : ValidationStates.Error;

      return { validationState, errorMessage };
    };

    updateDateValidationState = (dateValueFromInput: string) => {
      const dateIncompleteError = 'The date specified is invalid. Enter a year YYYY.';
      const isCompleteDate = dateValueFromInput.length === 4;
      const isDateEmpty = dateValueFromInput.length === 0;

      const validationData = this.getValidationData(this.getFormattedDate(dateValueFromInput));

      const errorMessage = !isCompleteDate && !isDateEmpty ? dateIncompleteError : validationData.errorMessage;
      const validationState =
        !isCompleteDate && !isDateEmpty && validationData.validationState === ValidationStates.Success
          ? ValidationStates.Error
          : validationData.validationState;

      this.setState({
        validationState,
        errorMessage: validationState !== ValidationStates.Success ? errorMessage : null
      });

      this.props.onYearValid(
        validationState === ValidationStates.Success,
        dateValueFromInput,
        isCompleteDate,
        validationState !== ValidationStates.Success ? errorMessage : null
      );
    };
  };
}

function generateYearInputWithSizes() {
  const DefaultYearInput = createYearInput(InputSizes.Default);
  return Object.assign(
    DefaultYearInput,
    ...Object.keys(InputSizes).map((sizeType) => ({
      [sizeType]: createYearInput(InputSizes[sizeType])
    }))
  );
}

export const YearInput = generateYearInputWithSizes();
